import React, { useEffect, useState } from "react";
import { Alert, Box, Button as MuiButton, TextField } from "@mui/material";
import ModalBase from "./ui/ModalBase";
import { useToastContext } from "../context/ToastContextProvider";
import { changeMyPassword, extractApiError } from "../api/userAdminApi";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Kullanıcının kendi parolasını değiştirmesi. Yönetici yetkisi gerekmez; mevcut parola
 * Keycloak'a karşı doğrulanır. Yönetici tarafından üretilen parola kalıcıdır, kullanıcı
 * dilediği zaman buradan değiştirir.
 */
const ChangePasswordModal: React.FC<Props> = ({ open, onClose }) => {
  const { showToast } = useToastContext();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCurrent("");
    setNext("");
    setRepeat("");
    setError(null);
  }, [open]);

  const handleSave = async () => {
    if (!current.trim() || !next.trim()) {
      setError("Mevcut ve yeni parola zorunludur.");
      return;
    }
    if (next.trim().length < 8) {
      setError("Yeni parola en az 8 karakter olmalıdır.");
      return;
    }
    if (next !== repeat) {
      setError("Yeni parolalar birbiriyle eşleşmiyor.");
      return;
    }
    if (next === current) {
      setError("Yeni parola mevcut parolayla aynı olamaz.");
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await changeMyPassword(current, next);
      showToast("Parolanız değiştirildi", "success");
      onClose();
    } catch (err: any) {
      setError(extractApiError(err, "Parola değiştirilemedi"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Parola Değiştir"
      maxWidth="xs"
      actions={
        <>
          <MuiButton onClick={onClose} disabled={saving}>
            Vazgeç
          </MuiButton>
          <MuiButton variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Kaydediliyor…" : "Değiştir"}
          </MuiButton>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Mevcut parola"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          size="small"
          autoComplete="current-password"
          fullWidth
        />
        <TextField
          label="Yeni parola"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          size="small"
          autoComplete="new-password"
          helperText="En az 8 karakter."
          fullWidth
        />
        <TextField
          label="Yeni parola (tekrar)"
          type="password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
          size="small"
          autoComplete="new-password"
          fullWidth
        />
      </Box>
    </ModalBase>
  );
};

export default ChangePasswordModal;
